import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useApolloClient as useApolloContextClient } from '@apollo/client';
import { useTheme } from 'styled-components';

import PhotoIdUpload from 'checkout/components/sections/user/photo-id-upload';
import useStores from 'shared/hooks/use-stores';
import { useInstoreEnrollmentAnalytics } from '../../hooks/use-instore-enrollment-analytics';
import { FieldWrapper, QuestionText } from './styles';

type PhotoUploadInputProps = {
  name: string;
  question: string;
};

// Reuses the same secure-image-upload flow (compression + uploadSecureImage mutation,
// S3 key returned) already used for ID/medical-card photo capture during checkout.
export const PhotoUploadInput = ({ name, question }: PhotoUploadInputProps): JSX.Element => {
  const { control, formState } = useFormContext();
  const hasError = !!formState.errors[name];
  const errorMessage = formState.errors[name]?.message as string | undefined;
  const { logError } = useInstoreEnrollmentAnalytics();
  // Deliberately styled via inline `style` (theme values + CSS custom properties with a
  // fallback, per CLAUDE.md) instead of a new styled-components component. This file already
  // gets eagerly evaluated by a lot of unrelated code through the med-intake barrel exports
  // (src/components/instore-enrollment/index.ts is re-exported from far outside this feature,
  // e.g. use-dutchie-pay-analytics.tsx), and styled-components assigns each `styled.xxx` call a
  // sequential componentId across that whole shared module graph -- so adding new styled()
  // calls here shifts the generated classnames (and breaks snapshot tests) for many unrelated
  // components elsewhere in the app that happen to be evaluated later in the same graph.
  const { colors, spaces } = useTheme();

  // PhotoIdUpload reads its Apollo client off the MobX store (shared/hooks/use-apollo-client),
  // which only the checkout flow populates (checkout/isolated/use-store-map.ts). Outside
  // checkout that property is never set, so mirror the same assignment here.
  const stores = useStores();
  const apolloClient = useApolloContextClient();
  stores.apolloClient = apolloClient;

  const handleUploadError = (error: unknown): void => {
    logError('Drivers license/MMJID photo upload failed', {
      field: name,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
  };

  return (
    <FieldWrapper id={name}>
      <QuestionText>{question}</QuestionText>
      <div
        style={
          hasError
            ? {
                border: `1px solid var(--dcm-med-intake-photo-upload-error-border-color, ${colors.red[55]})`,
                borderRadius: 'var(--dcm-med-intake-photo-upload-error-border-radius, 4px)',
                padding: spaces[4],
              }
            : undefined
        }
      >
        <Controller
          name={name}
          control={control}
          defaultValue=''
          render={({ value, onChange }) => (
            <PhotoIdUpload
              id={name}
              value={value}
              guest={false}
              medical={false}
              error={hasError}
              onPhotoUploaded={onChange}
              onUploadError={handleUploadError}
              onPhotoReset={() => onChange('')}
              onValueAfterError={() => undefined}
            />
          )}
        />
      </div>
      {hasError && errorMessage && (
        <p
          role='alert'
          style={{
            color: `var(--dcm-med-intake-photo-upload-error-text-color, ${colors.red[60]})`,
            fontSize: '12px',
            margin: `${spaces[2]} 0 0`,
          }}
        >
          {errorMessage}
        </p>
      )}
    </FieldWrapper>
  );
};
